import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Briefcase, Sparkles, Folder, ShieldCheck,
  FileText, Award, Save, X, AlertTriangle, CheckCircle2,
  Plus, Trash2, UserMinus, UserCheck, Mail, Phone, Building2, Calendar, Wallet
} from 'lucide-react';

// Tab list is filtered per-user. Employees don't see Billing & GST (they
// don't raise invoices) — they get a "Compensation" tab instead with the
// reverse salary calculator + payslip preview. Vendors / freelancers keep
// the GST flow unchanged. Documents tab also conditionally hides the GST
// Registration upload for employees (founder spec May 9, 2026).
const TABS_BASE = [
  { key: 'overview', label: 'Overview', icon: User },
  { key: 'professional', label: 'Professional', icon: Briefcase },
  { key: 'skills', label: 'Skills', icon: Sparkles },
  { key: 'projects', label: 'Projects', icon: Folder },
  { key: 'access', label: 'Access', icon: ShieldCheck, important: true },
  { key: 'leaves', label: 'Leaves', icon: Calendar },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'compensation', label: 'Compensation', icon: Wallet, employeeOnly: true },
  { key: 'billing', label: 'Billing & GST', icon: Building2, vendorOnly: true },
  { key: 'performance', label: 'Performance', icon: Award },
];

function tabsForWorkforce(workforceType) {
  const isEmployee = workforceType === 'employee';
  return TABS_BASE.filter(t => {
    if (t.employeeOnly && !isEmployee) return false;
    if (t.vendorOnly && isEmployee) return false;
    return true;
  });
}

const PERMISSION_COLORS = {
  view: 'bg-zinc-700/30 text-zinc-300',
  edit: 'bg-blue-500/15 text-blue-400',
  upload: 'bg-amber-500/15 text-amber-400',
  approve: 'bg-emerald-500/15 text-emerald-400',
};

const STATUS_COLORS = {
  active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  on_leave: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  inactive: 'bg-zinc-700/30 text-zinc-300 border-zinc-700/50',
  terminated: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
};

export default function PersonProfilePage() {
  const { id } = useParams();
  const { api, user: me } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [vocab, setVocab] = useState(null);
  const [active, setActive] = useState('overview');
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [showLifecycle, setShowLifecycle] = useState(false);
  const [showGrant, setShowGrant] = useState(false);
  const [projects, setProjects] = useState([]);

  const isAdmin = me?.role === 'master_admin' || me?.role === 'hr_manager';
  const isSelf = me?.user_id === id || me?.id === id;
  const canEditAdmin = isAdmin;

  const load = () => {
    api.get(`/hr/people/${id}`).then(r => { setProfile(r.data); setEdit({}); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    api.get('/hr/vocabularies').then(r => setVocab(r.data));
    api.get('/projects').then(r => setProjects(r.data));
  }, [api]);

  if (!profile || !vocab) return <div className="text-zinc-500 text-sm py-8 text-center" data-testid="profile-loading">Loading profile…</div>;

  const o = profile.overview, pro = profile.professional, sk = profile.skills,
        prj = profile.projects, acc = profile.access, doc = profile.documents,
        billing = profile.billing || {}, perf = profile.performance, pc = profile.profile_completion;

  const VENDOR_ROLES = new Set(['vendor_artist', 'vendor_producer', 'freelancer', 'vendor_admin']);
  const isVendorRole = VENDOR_ROLES.has(o?.role);
  const idLabel = isVendorRole ? 'Vendor ID' : 'Employee ID';

  const save = async () => {
    if (!Object.keys(edit).length) return;
    setSaving(true);
    try { await api.put(`/hr/people/${id}`, edit); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="person-profile-page">
      <button onClick={() => nav('/hr')} className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-white" data-testid="back-to-hr">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to HR Command Center
      </button>

      {/* Profile header */}
      <div className="bg-gradient-to-r from-cyan-900/20 via-blue-900/15 to-violet-900/20 border border-cyan-500/30 rounded-sm p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-xl font-heading font-bold uppercase">
              {(o.full_name || '?').split(' ').map(n => n[0]).slice(0, 2).join('')}
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-heading font-bold text-white" data-testid="profile-name">{o.full_name || '—'}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="text-[10px] text-zinc-400 flex items-center gap-1"><Mail className="w-3 h-3" />{o.email}</span>
                {o.phone && <span className="text-[10px] text-zinc-400 flex items-center gap-1"><Phone className="w-3 h-3" />{o.phone}</span>}
                <span className="text-[9px] text-zinc-500 font-mono">ID:{o.employee_id}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-400">{o.workforce_type || 'unset'}</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${pro.role_color}20`, color: pro.role_color }}>
                  {pro.role_label || pro.role || '—'}
                </span>
                <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${STATUS_COLORS[o.lifecycle_status] || STATUS_COLORS.active}`} data-testid="profile-status">
                  {o.lifecycle_status}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {/* Profile completion ring */}
            <div className="text-right">
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-heading font-bold ${pc.complete ? 'text-emerald-400' : 'text-amber-400'}`}>{pc.score}%</span>
                {pc.complete ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-amber-400" />}
              </div>
              <p className="text-[9px] text-zinc-500 uppercase">Profile Completion</p>
            </div>
            {canEditAdmin && (
              <button onClick={() => setShowLifecycle(true)} data-testid="open-lifecycle"
                className="text-[10px] px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-sm text-zinc-300 hover:border-rose-500/50 hover:text-white flex items-center gap-1.5">
                <UserMinus className="w-3 h-3" /> Change Status
              </button>
            )}
          </div>
        </div>
        {!pc.complete && (
          <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded-sm flex items-start gap-2" data-testid="incomplete-notice">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5" />
            <p className="text-[10px] text-amber-300">
              <span className="font-medium">Incomplete profile.</span> Missing: {pc.missing.join(', ')}.
            </p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 overflow-x-auto" data-testid="profile-tabs">
        {tabsForWorkforce(o.workforce_type).map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setActive(t.key)} data-testid={`tab-${t.key}`}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 ${active === t.key ? (t.important ? 'border-fuchsia-500 text-fuchsia-300' : 'border-white text-white') : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
              {t.important && active !== t.key && <span className="w-1 h-1 rounded-full bg-fuchsia-400" />}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {active === 'overview' && <OverviewTab o={o} edit={edit} setEdit={setEdit} canEdit={isAdmin || isSelf} save={save} saving={saving} idLabel={idLabel} />}
      {active === 'professional' && <ProfessionalTab pro={pro} vocab={vocab} edit={edit} setEdit={setEdit} canEdit={canEditAdmin} api={api} save={save} saving={saving} />}
      {active === 'skills' && <SkillsTab sk={sk} vocab={vocab} edit={edit} setEdit={setEdit} canEdit={isAdmin || isSelf} save={save} saving={saving} />}
      {active === 'projects' && <ProjectsTab prj={prj} />}
      {active === 'access' && <AccessTab acc={acc} api={api} userId={id} canEdit={canEditAdmin} reload={load} projects={projects} setShowGrant={setShowGrant} showGrant={showGrant} />}
      {active === 'leaves' && <LeavesTabHR userId={id} api={api} canEdit={canEditAdmin} userName={o.full_name || o.email} />}
      {active === 'documents' && <DocumentsTab doc={doc} workforce={o.workforce_type} userId={id} api={api} canEdit={isAdmin || isSelf} onSaved={load} />}
      {active === 'compensation' && <CompensationTab userId={id} api={api} canEdit={isAdmin} userName={o.full_name || o.email} currentCtc={o.current_ctc} onSaved={load} />}
      {active === 'billing' && <BillingTab billing={billing} userId={id} api={api} canEdit={isAdmin} onSaved={load} />}
      {active === 'performance' && <PerformanceTab perf={perf} />}

      {showLifecycle && (
        <LifecycleModal currentStatus={o.lifecycle_status} userId={id} api={api}
          onClose={() => setShowLifecycle(false)} onDone={() => { setShowLifecycle(false); load(); }} />
      )}
    </div>
  );
}

// ─────────────────────────── OVERVIEW ───────────────────────────
const OverviewTab = ({ o, edit, setEdit, canEdit, save, saving, idLabel }) => (
  <div className="grid grid-cols-2 gap-4" data-testid="overview-tab">
    <Field label="Full Name" value={edit.full_name ?? o.full_name} onChange={canEdit ? (v) => setEdit(e => ({ ...e, full_name: v })) : null} testid="ov-full-name" />
    <Field label="Email" value={o.email} readOnly testid="ov-email" />
    <Field label="Phone" value={edit.phone ?? o.phone ?? ''} onChange={canEdit ? (v) => setEdit(e => ({ ...e, phone: v })) : null} testid="ov-phone" />
    <Field label={idLabel} value={o.employee_id} readOnly testid="ov-emp-id" />
    <Field label="Workforce Type" value={o.workforce_type || '—'} readOnly testid="ov-type" />
    <Field label="Created" value={o.created_at?.slice(0, 10) || '—'} readOnly testid="ov-created" />
    {canEdit && Object.keys(edit).length > 0 && (
      <div className="col-span-2"><SaveBar onSave={save} saving={saving} /></div>
    )}
  </div>
);

// ─────────────────────────── PROFESSIONAL ───────────────────────────
const ProfessionalTab = ({ pro, vocab, edit, setEdit, canEdit, api, save, saving }) => {
  const [managers, setManagers] = useState([]);
  useEffect(() => { api.get('/hr/managers').then(r => setManagers(r.data.managers || [])); }, [api]);
  return (
    <div className="grid grid-cols-2 gap-4" data-testid="professional-tab">
      <SelectField label="Department" value={edit.department ?? pro.department ?? ''}
        onChange={canEdit ? (v) => setEdit(e => ({ ...e, department: v })) : null}
        options={vocab.departments} testid="prof-dept" />
      <SelectField label="Role" value={edit.role ?? pro.role ?? ''}
        onChange={canEdit ? (v) => setEdit(e => ({ ...e, role: v })) : null}
        options={vocab.roles.map(r => ({ value: r.key, label: r.label }))} testid="prof-role" />
      <SelectField label="Reporting Manager" value={edit.reporting_manager_id ?? pro.reporting_manager_id ?? ''}
        onChange={canEdit ? (v) => setEdit(e => ({ ...e, reporting_manager_id: v })) : null}
        options={[{ value: '', label: '— None —' }, ...managers.map(m => ({ value: m.id, label: `${m.full_name} · ${m.role}` }))]}
        testid="prof-manager" />
      <Field label="Joining Date" type="date" value={edit.joining_date ?? pro.joining_date ?? ''}
        onChange={canEdit ? (v) => setEdit(e => ({ ...e, joining_date: v })) : null} testid="prof-joining" />
      <Field label="Contract Period" value={edit.contract_period ?? pro.contract_period ?? ''}
        onChange={canEdit ? (v) => setEdit(e => ({ ...e, contract_period: v })) : null}
        placeholder="e.g. 12 months" testid="prof-contract-period" />
      <Field label="Contract End" type="date" value={edit.contract_end_date ?? pro.contract_end_date ?? ''}
        onChange={canEdit ? (v) => setEdit(e => ({ ...e, contract_end_date: v })) : null} testid="prof-contract-end" />
      {canEdit && Object.keys(edit).length > 0 && (
        <div className="col-span-2"><SaveBar onSave={save} saving={saving} /></div>
      )}
    </div>
  );
};

// ─────────────────────────── SKILLS ───────────────────────────
const SkillsTab = ({ sk, vocab, edit, setEdit, canEdit, save, saving }) => {
  const skills = edit.skills ?? sk.skills;
  const skillLevels = edit.skill_levels ?? sk.skill_levels;
  const tools = edit.tools ?? sk.tools;
  const toggle = (key, item) => {
    const cur = (key === 'skills' ? skills : tools) || [];
    const next = cur.includes(item) ? cur.filter(s => s !== item) : [...cur, item];
    setEdit(e => ({ ...e, [key]: next }));
  };
  const setLevel = (skill, level) => {
    setEdit(e => ({ ...e, skill_levels: { ...(skillLevels || {}), [skill]: level } }));
  };
  return (
    <div className="space-y-5" data-testid="skills-tab">
      <div>
        <h3 className="text-xs font-heading font-semibold text-white mb-2">Skills · pick & rate 1–5</h3>
        <div className="flex flex-wrap gap-1.5">
          {vocab.skills.map(s => {
            const on = skills?.includes(s);
            const level = skillLevels?.[s] || 3;
            return (
              <div key={s} className={`px-2 py-1 rounded-sm border text-[10px] ${on ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300' : 'bg-zinc-900 border-zinc-800 text-zinc-500'}`}>
                <button onClick={() => canEdit && toggle('skills', s)} disabled={!canEdit} data-testid={`skill-${s}`}
                  className="capitalize">{s.replace(/_/g, ' ')}</button>
                {on && (
                  <span className="ml-2 inline-flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button key={n} onClick={() => canEdit && setLevel(s, n)} disabled={!canEdit}
                        data-testid={`skill-level-${s}-${n}`}
                        className={`w-1.5 h-1.5 rounded-full ${n <= level ? 'bg-cyan-400' : 'bg-zinc-700'}`} />
                    ))}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <h3 className="text-xs font-heading font-semibold text-white mb-2">Tools</h3>
        <div className="flex flex-wrap gap-1.5">
          {vocab.tools.map(t => {
            const on = tools?.includes(t);
            return (
              <button key={t} onClick={() => canEdit && toggle('tools', t)} disabled={!canEdit}
                data-testid={`tool-${t}`}
                className={`px-2 py-1 rounded-sm border text-[10px] ${on ? 'bg-violet-500/15 border-violet-500/40 text-violet-300' : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>
                {t}
              </button>
            );
          })}
        </div>
      </div>
      {canEdit && Object.keys(edit).length > 0 && <SaveBar onSave={save} saving={saving} />}
    </div>
  );
};

// ─────────────────────────── PROJECTS ───────────────────────────
const ProjectsTab = ({ prj }) => (
  <div className="grid grid-cols-2 gap-4" data-testid="projects-tab">
    <Card title={`Active Projects (${prj.active.length})`}>
      {prj.active.length === 0 && <p className="text-[10px] text-zinc-600">No active project assignments</p>}
      {prj.active.map(p => (
        <div key={p.id} className="flex items-center justify-between py-2 border-b border-zinc-800/40 last:border-0">
          <div>
            <p className="text-xs text-zinc-200">{p.name}</p>
            <p className="text-[10px] text-zinc-500">{p.status} · deadline {p.deadline?.slice(0, 10) || 'TBD'}</p>
          </div>
        </div>
      ))}
    </Card>
    <Card title={`Past Projects (${prj.past.length})`}>
      {prj.past.length === 0 && <p className="text-[10px] text-zinc-600">No past project history</p>}
      {prj.past.map(p => (
        <div key={p.id} className="py-1.5 text-xs text-zinc-400">{p.name}</div>
      ))}
    </Card>
    <Card title={`Active Tasks (${prj.active_task_count})`} className="col-span-2">
      {prj.active_tasks.length === 0 && <p className="text-[10px] text-zinc-600">No active tasks assigned</p>}
      {prj.active_tasks.map(t => (
        <div key={t.id} className="flex items-center justify-between py-2 border-b border-zinc-800/40 last:border-0">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-zinc-200">{t.title}</p>
            <p className="text-[10px] text-zinc-500">{t.task_type} · {t.parent_code || 'general'} · status {t.status}</p>
          </div>
          {t.deadline && <span className="text-[10px] text-amber-400">{t.deadline.slice(0, 10)}</span>}
        </div>
      ))}
    </Card>
  </div>
);

// ─────────────────────────── ACCESS (3 layers) ───────────────────────────
const AccessTab = ({ acc, api, userId, canEdit, reload, projects, showGrant, setShowGrant }) => {
  const [systemEdit, setSystemEdit] = useState(null);

  const beginEdit = () => setSystemEdit({ ...acc.system_modules });
  const saveSystem = async () => {
    await api.put(`/hr/people/${userId}/access/system`, { overrides: systemEdit });
    setSystemEdit(null); reload();
  };
  const revoke = async (pid) => {
    if (!window.confirm('Revoke this project access?')) return;
    await api.delete(`/hr/people/${userId}/access/projects/${pid}`);
    reload();
  };

  return (
    <div className="space-y-4" data-testid="access-tab">
      {/* Layer 1: System modules */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-xs font-heading font-semibold text-white">Layer 1 · System Module Access</h3>
            <p className="text-[10px] text-zinc-500">Which menus this person can see across VawworldOS.</p>
          </div>
          {canEdit && systemEdit === null && (
            <button onClick={beginEdit} className="text-[10px] px-2 py-1 rounded bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/30" data-testid="edit-system-access">Edit</button>
          )}
          {canEdit && systemEdit !== null && (
            <div className="flex gap-1">
              <button onClick={() => setSystemEdit(null)} className="text-[10px] px-2 py-1 rounded bg-zinc-800 text-zinc-300">Cancel</button>
              <button onClick={saveSystem} data-testid="save-system-access" className="text-[10px] px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/30">Save</button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-4 gap-2">
          {Object.keys(acc.system_modules).sort().map(m => {
            const on = systemEdit ? systemEdit[m] : acc.system_modules[m];
            return (
              <button key={m} onClick={() => systemEdit !== null && setSystemEdit(s => ({ ...s, [m]: !s[m] }))}
                disabled={systemEdit === null}
                data-testid={`mod-${m}`}
                className={`text-[10px] px-2 py-1.5 rounded text-left flex items-center justify-between border ${on ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-zinc-950 border-zinc-800 text-zinc-600'}`}>
                <span className="capitalize">{m.replace(/_/g, ' ')}</span>
                {on ? '✓' : '✗'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Layer 2 + 3: Project access */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-xs font-heading font-semibold text-white">Layer 2 · Project Membership <span className="text-zinc-500">+</span> Layer 3 · Per-Project Permission</h3>
            <p className="text-[10px] text-zinc-500">Each project carries one permission level: view / edit / upload / approve.</p>
          </div>
          {canEdit && (
            <button onClick={() => setShowGrant(true)} data-testid="grant-project-btn"
              className="text-[10px] px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/30 flex items-center gap-1">
              <Plus className="w-3 h-3" /> Grant Project
            </button>
          )}
        </div>
        {acc.project_access.length === 0 ? (
          <p className="text-[10px] text-zinc-600 italic py-3">No project access granted yet</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800/40 text-[9px] text-zinc-500 uppercase">
                <th className="text-left py-1.5">Project</th>
                <th className="text-left">Permission</th>
                <th className="text-left">Granted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {acc.project_access.map(p => {
                const perms = p.permission_levels || [p.permission_level];
                return (
                  <tr key={p.project_id} className="border-b border-zinc-800/30 last:border-0">
                    <td className="py-1.5 text-[11px] text-zinc-200">{p.project_name}</td>
                    <td>
                      <div className="flex gap-1 flex-wrap">
                        {perms.map(lv => (
                          <span key={lv} className={`text-[8px] uppercase px-1.5 py-0.5 rounded ${PERMISSION_COLORS[lv] || PERMISSION_COLORS.view}`}>{lv}</span>
                        ))}
                      </div>
                    </td>
                    <td className="text-[10px] text-zinc-500">{p.granted_at?.slice(0, 10)}</td>
                    <td className="text-right">
                      {canEdit && (
                        <button onClick={() => revoke(p.project_id)} data-testid={`revoke-${p.project_id}`}
                          className="text-[10px] px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 hover:bg-rose-500/30">
                          <Trash2 className="w-3 h-3 inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showGrant && (
        <GrantProjectModal projects={projects} api={api} userId={userId}
          onClose={() => setShowGrant(false)} onDone={() => { setShowGrant(false); reload(); }}
          permissionLevels={acc.permission_levels} targetRole={acc.role} />
      )}
    </div>
  );
};

// ─────────────────────────── DOCUMENTS ───────────────────────────
// Employees never raise invoices, so the GST Registration card is hidden
// for them (founder spec May 9, 2026). Vendors / freelancers see it.
const DOC_FIELDS = [
  { kind: 'nda',             label: 'NDA',                statusKey: 'nda_status' },
  { kind: 'contract',        label: 'Contract / SoW',     statusKey: null },
  { kind: 'id_proof',        label: 'ID Proof',           statusKey: null },
  { kind: 'pan',             label: 'PAN Card',           statusKey: null },
  { kind: 'aadhar',          label: 'Aadhaar',            statusKey: null },
  { kind: 'gst_certificate', label: 'GST Registration',   statusKey: null, vendorOnly: true },
];
const DocumentsTab = ({ doc, workforce, userId, api, canEdit, onSaved }) => {
  const isEmployee = workforce === 'employee';
  const visibleDocs = DOC_FIELDS.filter(f => !f.vendorOnly || !isEmployee);
  return (
  <div className="space-y-4" data-testid="documents-tab">
    <p className="text-[10px] text-zinc-500 uppercase tracking-widest">KYC + Compliance</p>
    <div className="grid grid-cols-2 gap-3">
      {visibleDocs.map(f => (
        <DocUploadCard
          key={f.kind}
          kind={f.kind}
          label={f.label}
          urlField={`${f.kind}_url`}
          metaField={`${f.kind}_meta`}
          ndaStatus={f.statusKey ? doc[f.statusKey] : null}
          doc={doc}
          userId={userId}
          api={api}
          canEdit={canEdit}
          onSaved={onSaved}
        />
      ))}
    </div>
    {workforce === 'vendor' && (doc.bank_account || doc.bank_ifsc) && (
      <Card title="Bank Details">
        <p className="text-[10px] text-zinc-500">A/C: {doc.bank_account || '—'} · IFSC: {doc.bank_ifsc || '—'}</p>
      </Card>
    )}
  </div>
  );
};

const DocUploadCard = ({ kind, label, urlField, metaField, ndaStatus, doc, userId, api, canEdit, onSaved }) => {
  const url = doc[urlField];
  const meta = doc[metaField] || {};
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const fileRef = React.useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      await api.post(`/hr/people/${userId}/documents/${kind}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved && onSaved();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    setBusy(true); setErr('');
    try {
      await api.delete(`/hr/people/${userId}/documents/${kind}`);
      onSaved && onSaved();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  const status = url ? (ndaStatus || 'uploaded') : 'pending';
  const ok = status === 'signed' || status === 'approved' || status === 'uploaded';

  return (
    <div className="border border-zinc-800 rounded-sm p-3 bg-zinc-900/40" data-testid={`doc-card-${kind}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold text-zinc-200">{label}</span>
        <div className="flex items-center gap-1">
          {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
          <span className={`text-[9px] uppercase ${ok ? 'text-emerald-400' : 'text-amber-400'}`}>{status}</span>
        </div>
      </div>
      {url ? (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <a href={url} target="_blank" rel="noreferrer" className="text-[10px] text-cyan-400 hover:underline truncate flex-1" title={meta.filename}>
              {meta.filename || 'View'}
            </a>
            {meta.size && <span className="text-[9px] text-zinc-600">{(meta.size/1024).toFixed(0)}KB</span>}
          </div>
          {meta.uploaded_at && (
            <p className="text-[9px] text-zinc-500">
              Uploaded {new Date(meta.uploaded_at).toLocaleDateString()} · {meta.uploaded_by_name || ''}
            </p>
          )}
          {canEdit && (
            <div className="flex gap-2 pt-1">
              <label className="text-[10px] text-zinc-400 hover:text-white cursor-pointer">
                <input ref={fileRef} type="file" className="hidden" onChange={onFile} accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" data-testid={`doc-replace-${kind}`} />
                {busy ? <span className="text-zinc-500">Uploading…</span> : 'Replace'}
              </label>
              <button onClick={onDelete} disabled={busy} className="text-[10px] text-rose-400 hover:text-rose-300" data-testid={`doc-delete-${kind}`}>Delete</button>
            </div>
          )}
        </div>
      ) : (
        canEdit ? (
          <label className="block w-full border border-dashed border-zinc-700 hover:border-amber-400 hover:bg-amber-500/5 rounded-sm py-3 text-center cursor-pointer transition-colors">
            <input ref={fileRef} type="file" className="hidden" onChange={onFile} accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" data-testid={`doc-upload-${kind}`} />
            {busy ? (
              <span className="text-[10px] text-zinc-400">Uploading…</span>
            ) : (
              <span className="text-[10px] text-zinc-400 hover:text-amber-300">+ Upload</span>
            )}
          </label>
        ) : (
          <p className="text-[10px] text-zinc-600 italic py-2">Not uploaded yet</p>
        )
      )}
      {err && <p className="text-[9px] text-rose-400 mt-1" data-testid={`doc-err-${kind}`}>{err}</p>}
    </div>
  );
};

// ─────────────────────────── BILLING & GST ───────────────────────────
const BillingTab = ({ billing, userId, api, canEdit, onSaved }) => {
  const [form, setForm] = React.useState(() => ({ ...billing }));
  React.useEffect(() => { setForm({ ...billing }); }, [billing]);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [saved, setSaved] = React.useState(false);

  const onGstin = (val) => {
    const v = (val || '').toUpperCase().slice(0, 15);
    const update = { vendor_gstin: v };
    if (v.length >= 12) update.vendor_pan = v.slice(2, 12);
    if (v.length >= 2) update.vendor_state_code = v.slice(0, 2);
    setForm(prev => ({ ...prev, ...update }));
  };

  const save = async () => {
    if (form.vendor_gstin && form.vendor_gstin.length !== 15) {
      setErr('GSTIN must be exactly 15 chars'); return;
    }
    setSaving(true); setErr(''); setSaved(false);
    try {
      await api.put(`/users/${userId}/billing-profile`, form);
      setSaved(true);
      onSaved && onSaved();
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const F = (key, placeholder, opts = {}) => (
    <input
      type="text" placeholder={placeholder}
      value={form[key] || ''}
      onChange={e => {
        let v = e.target.value;
        if (opts.upper) v = v.toUpperCase();
        if (opts.maxLength) v = v.slice(0, opts.maxLength);
        setForm({ ...form, [key]: v });
      }}
      readOnly={!canEdit}
      data-testid={`billing-${key}`}
      className={`h-9 w-full bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white ${opts.mono ? 'font-mono' : ''} ${!canEdit ? 'opacity-60' : ''}`}
    />
  );

  return (
    <div className="space-y-3" data-testid="billing-tab">
      <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Bill-From block · auto-stamped on every new invoice</p>
      <div className="grid grid-cols-2 gap-2">
        {F('vendor_legal_name', 'Legal Name (e.g. SIDNEE PRODUCTIONS)')}
        <select
          value={form.vendor_constitution || ''}
          onChange={e => setForm({ ...form, vendor_constitution: e.target.value })}
          disabled={!canEdit}
          data-testid="billing-vendor_constitution"
          className={`h-9 w-full bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white ${!canEdit ? 'opacity-60' : ''}`}
        >
          <option value="">Constitution</option>
          <option>Proprietorship</option><option>Partnership</option>
          <option>Private Limited</option><option>LLP</option>
          <option>Public Limited</option><option>HUF</option><option>Individual</option>
        </select>
        <input
          type="text" placeholder="GSTIN (15 chars)"
          value={form.vendor_gstin || ''}
          onChange={e => onGstin(e.target.value)}
          maxLength={15} readOnly={!canEdit}
          data-testid="billing-vendor_gstin"
          className={`h-9 w-full bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white font-mono ${!canEdit ? 'opacity-60' : ''}`}
        />
        {F('vendor_pan', 'PAN (auto from GSTIN)', { upper: true, maxLength: 10, mono: true })}
        {F('vendor_contact_person', 'Contact Person')}
        {F('vendor_phone', 'Phone')}
      </div>
      {F('vendor_address_line_1', 'Address Line 1')}
      {F('vendor_address_line_2', 'Address Line 2 (locality)')}
      <div className="grid grid-cols-3 gap-2">
        {F('vendor_city', 'City')}
        {F('vendor_state_name', 'State')}
        {F('vendor_pin', 'PIN', { maxLength: 6 })}
      </div>
      {canEdit && (
        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={save} disabled={saving}
            data-testid="billing-save"
            className="h-9 px-4 text-[11px] font-semibold bg-amber-600 hover:bg-amber-500 text-white rounded-sm flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Billing Profile'}
          </button>
          {saved && <span className="text-[10px] text-emerald-300">Saved · syncs to /vendors collection + invoice billed_from</span>}
          {err && <span className="text-[10px] text-rose-300">{err}</span>}
        </div>
      )}
      {billing.billing_profile_updated_at && (
        <p className="text-[9px] text-zinc-600 italic">Last updated {new Date(billing.billing_profile_updated_at).toLocaleString()}</p>
      )}
    </div>
  );
};

// ─────────────────────────── PERFORMANCE ───────────────────────────
const PerformanceTab = ({ perf }) => (
  <div className="grid grid-cols-3 gap-3" data-testid="performance-tab">
    <Card title="KPI Score"><p className="text-3xl font-heading font-bold text-emerald-400">{perf.kpi_score}</p></Card>
    <Card title="Submissions"><p className="text-3xl font-heading font-bold text-cyan-400">{perf.submissions_total}</p></Card>
    <Card title="Approval Rate"><p className="text-3xl font-heading font-bold text-amber-400">{perf.approval_rate}%</p></Card>
    <Card title="Feedback Notes" className="col-span-3">
      {perf.feedback_notes.length === 0 ? <p className="text-[10px] text-zinc-600">No feedback recorded</p> : (
        perf.feedback_notes.map((n, i) => <p key={i} className="text-[11px] text-zinc-300 py-1 border-b border-zinc-800/40">{n.text || n}</p>)
      )}
    </Card>
    <Card title="Warnings" className="col-span-3">
      {perf.warnings.length === 0 ? <p className="text-[10px] text-zinc-600">No warnings issued</p> : (
        perf.warnings.map((n, i) => <p key={i} className="text-[11px] text-rose-300 py-1 border-b border-zinc-800/40">{n.text || n}</p>)
      )}
    </Card>
  </div>
);

// ─────────────────────────── Modals ───────────────────────────
const LifecycleModal = ({ currentStatus, userId, api, onClose, onDone }) => {
  const STATUSES = ['active', 'on_leave', 'inactive', 'terminated'];
  const [status, setStatus] = useState(currentStatus);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const change = async () => {
    setBusy(true);
    try { await api.put(`/hr/people/${userId}/lifecycle`, { status, reason }); onDone(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 w-[28rem]" onClick={e => e.stopPropagation()} data-testid="lifecycle-modal">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-sm font-heading font-semibold text-white">Change Lifecycle Status</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <p className="text-[10px] text-zinc-500 mb-3">
          Inactive / Terminated will <span className="text-rose-300">auto-revoke all project access</span>; login stays enabled (re-hire ready).
        </p>
        <div className="space-y-2 mb-4">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`lc-${s}`}
              className={`w-full text-left px-3 py-2 border rounded-sm text-xs ${status === s ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-300' : 'bg-zinc-950 border-zinc-800 text-zinc-400'}`}>
              {s.replace(/_/g, ' ').toUpperCase()}
              {s === currentStatus && <span className="text-[9px] text-zinc-500 ml-2">(current)</span>}
            </button>
          ))}
        </div>
        <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason / note (optional)..."
          rows={3} data-testid="lc-reason"
          className="w-full p-2 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none mb-3" />
        <button onClick={change} disabled={busy || status === currentStatus} data-testid="lc-confirm"
          className="w-full px-3 py-2 bg-rose-500 hover:bg-rose-400 disabled:opacity-40 rounded-sm text-xs text-white font-medium">
          {busy ? 'Updating…' : `Set status to ${status.replace('_', ' ')}`}
        </button>
      </div>
    </div>
  );
};

// Roles that legitimately need "approve" rights — everyone else only ever
// gets view/edit/upload (founder spec, May 2026 — Khojendra's profile bug).
const APPROVE_ROLES = new Set([
  'master_admin', 'coo', 'studio_director', 'creative_director',
  'art_director', 'animation_director', 'director',
  'vfx_supervisor', 'cg_supervisor', 'supervisor',
  'production_manager', 'executive_producer', 'producer', 'line_producer',
  'client_director',
]);

const GrantProjectModal = ({ projects, api, userId, onClose, onDone, permissionLevels, targetRole }) => {
  const [pid, setPid] = useState(projects[0]?.id || '');
  const showApprove = APPROVE_ROLES.has(targetRole);
  // Default: view + edit + upload checked for employees (per founder spec).
  // Approvers default to "approve" only — they can tick more if needed.
  const initialLevels = showApprove ? ['approve'] : ['view', 'edit', 'upload'];
  const [levels, setLevels] = useState(new Set(initialLevels));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const allLevels = (permissionLevels || ['view', 'edit', 'upload', 'approve']).filter(
    lv => showApprove || lv !== 'approve'
  );
  const toggle = (lv) => {
    const next = new Set(levels);
    if (next.has(lv)) next.delete(lv); else next.add(lv);
    setLevels(next);
  };
  const grant = async () => {
    setErr('');
    if (!pid) { setErr('Pick a project'); return; }
    if (levels.size === 0) { setErr('Select at least one permission'); return; }
    setBusy(true);
    try {
      await api.post(`/hr/people/${userId}/access/projects`, {
        project_id: pid,
        permission_levels: Array.from(levels),
      });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to grant');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 w-[28rem]" onClick={e => e.stopPropagation()} data-testid="grant-modal">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-sm font-heading font-semibold text-white">Grant Project Access</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <SelectField label="Project" value={pid} onChange={setPid} options={projects.map(p => ({ value: p.id, label: p.name }))} testid="grant-project" />
        <div className="mt-3">
          <label className="text-[9px] text-zinc-500 uppercase tracking-wider">Permissions</label>
          <p className="text-[10px] text-zinc-600 mb-2">{showApprove ? 'Tick all permissions to grant.' : 'Approve is reserved for production heads — not shown for this role.'}</p>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {allLevels.map(lv => (
              <label key={lv} data-testid={`grant-perm-${lv}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer text-[11px] transition-colors ${
                  levels.has(lv)
                    ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-200'
                    : 'border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700'
                }`}>
                <input
                  type="checkbox"
                  checked={levels.has(lv)}
                  onChange={() => toggle(lv)}
                  className="accent-emerald-500"
                />
                <span className="uppercase tracking-wider">{lv}</span>
              </label>
            ))}
          </div>
        </div>
        {err && <p className="text-[10px] text-rose-400 mt-2" data-testid="grant-error">{err}</p>}
        <button onClick={grant} disabled={busy} data-testid="grant-confirm"
          className="w-full mt-4 px-3 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 rounded-sm text-xs text-white font-medium">
          {busy ? 'Granting…' : `Grant ${levels.size} Permission${levels.size === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────── Helpers ───────────────────────────
const Field = ({ label, value, onChange, type = 'text', readOnly, placeholder, testid }) => (
  <div>
    <label className="text-[9px] text-zinc-500 uppercase tracking-wider">{label}</label>
    <input type={type} value={value || ''} onChange={onChange ? e => onChange(e.target.value) : undefined}
      readOnly={!onChange || readOnly} placeholder={placeholder} data-testid={testid}
      className={`w-full mt-1 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs ${readOnly || !onChange ? 'text-zinc-400' : 'text-white'} focus:outline-none focus:border-cyan-500/50`} />
  </div>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-[9px] text-zinc-500 uppercase tracking-wider">{label}</label>
    <select value={value || ''} onChange={onChange ? e => onChange(e.target.value) : undefined}
      disabled={!onChange} data-testid={testid}
      className="w-full mt-1 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none focus:border-cyan-500/50">
      <option value="">—</option>
      {options.map(o => {
        const v = typeof o === 'string' ? o : o.value;
        const l = typeof o === 'string' ? o.replace(/_/g, ' ') : o.label;
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  </div>
);

const Card = ({ title, children, className = '' }) => (
  <div className={`bg-zinc-900 border border-zinc-800 rounded-sm p-3 ${className}`}>
    <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-2">{title}</p>
    {children}
  </div>
);

// ─────────────────────────── COMPENSATION TAB (HR side) ───────────────────────────
// Employee-only view. Surfaces the current CTC breakdown + lets HR run the
// reverse calculator (target net → annual + components) and update the
// employee's salary in one click. Replaces the old "Billing & GST" tab for
// employees per founder spec May 9, 2026 — employees never raise invoices.
function CompensationTab({ userId, api, canEdit, userName, currentCtc, onSaved }) {
  const ctc = currentCtc || {};
  const fmtINR = (n) => n != null && !isNaN(n) ? `₹${Number(n).toLocaleString('en-IN')}` : '—';
  const hasCTC = !!ctc.annual;

  const [reverseNet, setReverseNet] = React.useState('');
  const [annualOverride, setAnnualOverride] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [err, setErr] = React.useState('');
  const [draft, setDraft] = React.useState(null); // staged breakdown awaiting Save

  const solveFromNet = async () => {
    setErr(''); setMsg('');
    const target = parseFloat(reverseNet) || 0;
    if (target <= 0) { setErr('Enter a positive monthly net amount'); return; }
    setBusy(true);
    try {
      const r = await api.post('/hr/ctc/from-net', { net_monthly: target });
      const meta = r.data._meta || {};
      const cleaned = { ...r.data };
      delete cleaned._meta;
      setDraft(cleaned);
      const pfMsg = meta.pf_included ? '' : ' · PF skipped (basic < ₹15k)';
      setMsg(`✓ Solved · annual ${fmtINR(cleaned.annual)} → net ${fmtINR(cleaned.net_monthly)}/mo${pfMsg}. Click "Save" below to apply.`);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not solve — try a different target');
    } finally {
      setBusy(false);
    }
  };

  const calcFromAnnual = async () => {
    setErr(''); setMsg('');
    const annual = parseFloat(annualOverride) || 0;
    if (annual <= 0) { setErr('Enter a positive annual CTC'); return; }
    setBusy(true);
    try {
      const r = await api.post('/hr/ctc/calculate', { annual });
      setDraft(r.data);
      setMsg(`✓ Calculated · gross ${fmtINR(r.data.gross_monthly)}/mo, net ${fmtINR(r.data.net_monthly)}/mo. Click "Save" below to apply.`);
    } catch (e) {
      setErr('Could not calculate');
    } finally {
      setBusy(false);
    }
  };

  const saveCompensation = async () => {
    if (!draft) return;
    setBusy(true);
    setErr(''); setMsg('');
    try {
      await api.put(`/hr/people/${userId}/compensation`, { current_ctc: draft });
      setMsg('✓ Saved — payslip will use this breakdown going forward.');
      setDraft(null);
      onSaved && onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const view = draft || ctc;

  return (
    <div className="space-y-4" data-testid="compensation-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-heading font-semibold text-white">Compensation</h3>
          <p className="text-[10px] text-zinc-500 mt-0.5">CTC breakdown for {userName}. Updating here writes to ``users.current_ctc`` and feeds the payslip generator.</p>
        </div>
      </div>

      {/* Reverse calculator card */}
      {canEdit && (
        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-md p-4" data-testid="hr-comp-reverse-block">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">Calculate from Net Take-home</span>
            <span className="text-[9px] text-zinc-400">— enter the monthly take-home you've promised; we solve the components.</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="text-[9px] text-zinc-500 uppercase tracking-wider">Net / Month (₹)</label>
                <input type="number" value={reverseNet}
                  onChange={(e) => setReverseNet(e.target.value)}
                  data-testid="hr-comp-reverse-net"
                  className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-emerald-500/40 rounded-md text-base text-white font-mono focus:outline-none focus:border-emerald-400"
                  placeholder="e.g. 10000" />
              </div>
              <button onClick={solveFromNet} disabled={busy} data-testid="hr-comp-reverse-solve"
                className="text-[11px] px-3 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 rounded-md text-white font-medium">
                {busy ? 'Solving…' : 'Solve'}
              </button>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="text-[9px] text-zinc-500 uppercase tracking-wider">…or by Annual CTC (₹)</label>
                <input type="number" value={annualOverride}
                  onChange={(e) => setAnnualOverride(e.target.value)}
                  data-testid="hr-comp-annual"
                  className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-violet-500/40 rounded-md text-base text-white font-mono focus:outline-none focus:border-violet-400"
                  placeholder="e.g. 800000" />
              </div>
              <button onClick={calcFromAnnual} disabled={busy} data-testid="hr-comp-recalc"
                className="text-[11px] px-3 py-2 bg-violet-500 hover:bg-violet-400 disabled:opacity-50 rounded-md text-white font-medium">
                Calc
              </button>
            </div>
          </div>
          {msg && <p className="text-[10px] mt-2 text-emerald-200" data-testid="hr-comp-msg">{msg}</p>}
          {err && <p className="text-[10px] mt-2 text-rose-300" data-testid="hr-comp-err">{err}</p>}
        </div>
      )}

      {/* Breakdown card — live (draft) or saved */}
      {hasCTC || draft ? (
        <div className={`bg-gradient-to-br ${draft ? 'from-amber-500/10 to-amber-700/5 border-amber-500/30' : 'from-emerald-500/10 to-emerald-700/5 border-emerald-500/30'} border rounded-md p-5`} data-testid="hr-comp-breakdown">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-heading font-semibold text-white">
              {draft ? 'Draft (unsaved)' : 'Current Breakdown'}
            </h4>
            {draft && canEdit && (
              <div className="flex gap-2">
                <button onClick={() => { setDraft(null); setMsg(''); }} className="text-[10px] px-2 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-200">
                  Discard
                </button>
                <button onClick={saveCompensation} disabled={busy} data-testid="hr-comp-save"
                  className="text-[10px] px-3 py-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 rounded text-white font-medium flex items-center gap-1">
                  <Save className="w-3 h-3" /> {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CompField label="Annual CTC"     v={fmtINR(view.annual)} highlight />
            <CompField label="Gross / Month"  v={fmtINR(view.gross_monthly)} />
            <CompField label="Net / Month"    v={fmtINR(view.net_monthly)} highlight />
            <CompField label="TDS / Month"    v={fmtINR(view.tds_monthly)} />
            <CompField label="Basic"          v={fmtINR(view.basic)} />
            <CompField label="HRA"            v={fmtINR(view.hra)} />
            <CompField label="Special Allow." v={fmtINR(view.special_allowance)} />
            <CompField label="PF (Employee)"  v={fmtINR(view.pf_employee)} />
          </div>
        </div>
      ) : (
        <p className="text-xs text-zinc-400 px-4 py-8 text-center bg-zinc-900 border border-zinc-800 rounded-md">
          No CTC configured yet. Use the calculator above to set one.
        </p>
      )}
    </div>
  );
}

const CompField = ({ label, v, highlight }) => (
  <div className={`px-3 py-2 rounded-md ${highlight ? 'bg-emerald-900/30 border border-emerald-500/30' : 'bg-zinc-900 border border-zinc-800'}`}>
    <p className="text-[9px] uppercase tracking-wider text-zinc-400">{label}</p>
    <p className={`text-sm font-mono mt-0.5 ${highlight ? 'text-emerald-200 font-semibold' : 'text-zinc-100'}`}>{v}</p>
  </div>
);

const SaveBar = ({ onSave, saving }) => (
  <div className="flex justify-end">
    <button onClick={onSave} disabled={saving} data-testid="profile-save-btn"
      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 rounded-sm text-xs text-white font-medium">
      <Save className="w-3 h-3" /> {saving ? 'Saving…' : 'Save Changes'}
    </button>
  </div>
);

// ─────────────────────────── LEAVES TAB (HR side) ───────────────────────────
// HR/Admin view of an employee's leave balance + Comp-Off audit log + Grant
// Comp-Off button. The same data the employee sees on their own dashboard,
// but with the +grant action attached.
const LEAVE_KIND_COLORS = {
  casual:  { card: 'border-cyan-500/30',    label: 'text-cyan-300' },
  sick:    { card: 'border-rose-500/30',    label: 'text-rose-300' },
  earned:  { card: 'border-emerald-500/30', label: 'text-emerald-300' },
};
function LeavesTabHR({ userId, api, canEdit, userName }) {
  const [data, setData] = useState(null);
  const [grants, setGrants] = useState([]);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const reload = async () => {
    try {
      const [b, g] = await Promise.all([
        api.get(`/hr/people/${userId}/leaves`).catch(async () => {
          // Fallback: HR endpoint not yet available — read the public balance
          // via impersonation-free admin path. We model this as a simple no-op:
          // we just show the comp-off log fetched separately.
          return { data: { balance: null } };
        }),
        api.get(`/hr/comp-off/log/${userId}`),
      ]);
      setData(b.data);
      setGrants(g.data.grants || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [userId]);
  const revoke = async (gid) => {
    if (!window.confirm('Revoke this comp-off grant? The employee will lose 1 EL day.')) return;
    await api.delete(`/hr/comp-off/${gid}`);
    reload();
  };
  return (
    <div className="space-y-4" data-testid="hr-leaves-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-heading font-semibold text-white">Leave & Comp-Off</h3>
          <p className="text-[10px] text-zinc-500 mt-0.5">
            Earned leave is granted only via Comp-Off (Sunday/holiday work). Casual & Sick auto-accrue 1/month.
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setShowGrantModal(true)} data-testid="hr-grant-comp-off-btn"
            className="h-8 px-3 bg-emerald-500 hover:bg-emerald-400 rounded-sm text-white text-xs font-medium flex items-center gap-1.5">
            <Plus className="w-3 h-3" /> Grant Comp-Off
          </button>
        )}
      </div>

      <div className="bg-zinc-900 border border-emerald-500/20 rounded-md overflow-hidden" data-testid="hr-comp-off-log">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h4 className="text-xs font-semibold text-emerald-200 uppercase">Comp-Off Audit Log</h4>
          <span className="text-[10px] text-emerald-400">{grants.length} grant{grants.length === 1 ? '' : 's'} · +{grants.reduce((s,g) => s + (g.days || 0), 0)} EL total</span>
        </div>
        {grants.length === 0 ? (
          <p className="text-xs text-zinc-500 text-center py-8">No comp-off grants on record. Click "Grant Comp-Off" above to issue the first one.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] text-zinc-400 uppercase">
                <th className="text-left px-4 py-2">Work Date</th>
                <th className="text-left px-4 py-2">+Days</th>
                <th className="text-left px-4 py-2">Reason</th>
                <th className="text-left px-4 py-2">Granted</th>
                <th className="text-left px-4 py-2">By</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/40">
              {grants.map(g => (
                <tr key={g.id}>
                  <td className="px-4 py-2 text-xs font-mono text-zinc-200">{g.work_date}</td>
                  <td className="px-4 py-2 text-xs font-mono text-emerald-300">+{g.days}</td>
                  <td className="px-4 py-2 text-xs text-zinc-300">{g.reason}</td>
                  <td className="px-4 py-2 text-[10px] font-mono text-zinc-500">{(g.granted_at || '').slice(0, 10)}</td>
                  <td className="px-4 py-2 text-[10px] text-zinc-400">{g.granted_by_email}</td>
                  <td className="px-4 py-2 text-right">
                    {canEdit && (
                      <button onClick={() => revoke(g.id)} data-testid={`hr-revoke-grant-${g.id}`}
                        className="text-[10px] px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 hover:bg-rose-500/30">
                        <Trash2 className="w-3 h-3 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showGrantModal && (
        <CompOffGrantModal userId={userId} userName={userName} api={api}
          onClose={() => setShowGrantModal(false)}
          onDone={() => { setShowGrantModal(false); reload(); }} />
      )}
    </div>
  );
}

function CompOffGrantModal({ userId, userName, api, onClose, onDone }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [workDate, setWorkDate] = useState(todayISO);
  const [days, setDays] = useState(1);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setErr('');
    if (!workDate) { setErr('Pick a work date'); return; }
    if (!days || days <= 0) { setErr('Days must be > 0'); return; }
    setBusy(true);
    try {
      await api.post('/hr/comp-off/grant', {
        user_id: userId,
        work_date: workDate,
        days: Number(days),
        reason: reason.trim(),
      });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not grant');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 w-[28rem]" onClick={e => e.stopPropagation()} data-testid="comp-off-grant-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-sm font-heading font-semibold text-white">Grant Comp-Off</h3>
            <p className="text-[10px] text-zinc-500 mt-0.5">to {userName}</p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-[9px] text-zinc-500 uppercase tracking-wider">Work Date</span>
            <input type="date" value={workDate} onChange={e => setWorkDate(e.target.value)} max={todayISO}
              data-testid="comp-off-work-date"
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-sm text-sm text-white font-mono focus:outline-none focus:border-emerald-400" />
          </label>
          <label className="block">
            <span className="text-[9px] text-zinc-500 uppercase tracking-wider">Days to Grant</span>
            <input type="number" step="0.5" min="0.5" max="5" value={days}
              onChange={e => setDays(e.target.value)}
              data-testid="comp-off-days"
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-sm text-sm text-white font-mono focus:outline-none focus:border-emerald-400" />
          </label>
          <label className="block">
            <span className="text-[9px] text-zinc-500 uppercase tracking-wider">Reason</span>
            <input type="text" value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. Worked Sunday for delivery"
              data-testid="comp-off-reason"
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-sm text-xs text-white focus:outline-none focus:border-emerald-400" />
          </label>
        </div>
        {err && <p className="text-[10px] text-rose-400 mt-2" data-testid="comp-off-error">{err}</p>}
        <button onClick={submit} disabled={busy} data-testid="comp-off-confirm"
          className="w-full mt-4 px-3 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 rounded-sm text-xs text-white font-medium">
          {busy ? 'Granting…' : `Grant +${days} EL`}
        </button>
      </div>
    </div>
  );
}
